// ── Pattern Executor Hook ─────────────────────────────────────
// React hook for executing thinking patterns via SSE.
// Replaces the need for separate useAutoRefine, useExperimentLoop
// hooks when a pattern is active.

import { useState, useRef, useCallback } from 'react';
import { authFetch } from './api';
import { buildFlowNode } from './useCanvasMode';

const API_URL = process.env.REACT_APP_API_URL || '';

export function usePatternExecutor({
  rawNodesRef,
  applyLayout,
  drillStackRef,
  dynamicTypesRef,
  dynamicConfigRef,
  setNodeCount,
  buildDynamicConfigFn,
}) {
  const [isExecuting, setIsExecuting] = useState(false);
  const [currentStage, setCurrentStage] = useState(null);
  const [currentRound, setCurrentRound] = useState(0);
  const [stageResults, setStageResults] = useState({});
  const [stageHistory, setStageHistory] = useState([]); // [{id, label, status, startedAt, completedAt, round, nodeCount, detail}]
  const [checkpoint, setCheckpoint] = useState(null);
  const [error, setError] = useState(null);
  const [executionId, setExecutionId] = useState(null);
  const [patternName, setPatternName] = useState('');
  const [startedAt, setStartedAt] = useState(null);
  const abortRef = useRef(null);
  const onCompleteRef = useRef(null);

  const execute = useCallback(async (patternIdOrWorkflow, idea, nodes, mode, config = {}, onComplete) => {
    // Accept either a patternId string or an inline workflow object (from orchestrator)
    const isInlineWorkflow = typeof patternIdOrWorkflow === 'object' && patternIdOrWorkflow !== null;
    const displayName = isInlineWorkflow
      ? (patternIdOrWorkflow.name || patternIdOrWorkflow.id || 'Workflow')
      : patternIdOrWorkflow.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

    setIsExecuting(true);
    setError(null);
    setStageResults({});
    setStageHistory([]);
    setCheckpoint(null);
    setCurrentStage(null);
    setCurrentRound(0);
    setPatternName(displayName);
    setStartedAt(new Date().toISOString());
    onCompleteRef.current = onComplete || null;

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      // Serialize nodes for the request
      const serializedNodes = (nodes || []).map(n => {
        const d = n.data || n;
        return {
          id: n.id || d.id,
          type: d.type,
          label: d.label,
          reasoning: d.reasoning,
          parentIds: d.parentIds || (d.parentId ? [d.parentId] : []),
        };
      });

      // Build request body — inline workflow or patternId
      const body = isInlineWorkflow
        ? { workflow: patternIdOrWorkflow, idea, nodes: serializedNodes, mode, ...config }
        : { patternId: patternIdOrWorkflow, idea, nodes: serializedNodes, mode, ...config };

      const res = await authFetch(`${API_URL}/api/pattern/execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: controller.signal,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error(err.error || `Pattern execution failed: ${res.status}`);
      }

      // Read SSE stream
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6).trim();
          if (data === '[DONE]') continue;

          try {
            const event = JSON.parse(data);
            handleSSEEvent(event);
          } catch { /* skip non-JSON */ }
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('Pattern executor error:', err);
      setError(err.message);
    } finally {
      setIsExecuting(false);
      setCurrentStage(null);
      abortRef.current = null;
    }
  }, [rawNodesRef, applyLayout, drillStackRef, dynamicTypesRef, setNodeCount]); // eslint-disable-line

  const handleSSEEvent = useCallback((event) => {
    // Pattern progress
    if (event._patternProgress) {
      const newStage = event.stage;
      const round = event.round || 0;
      setCurrentStage(newStage);
      setCurrentRound(round);
      // Track stage history with timestamps
      setStageHistory(prev => {
        const updated = prev.map(s =>
          s.status === 'active' ? { ...s, status: 'done', completedAt: new Date().toISOString() } : s
        );
        // Add new stage if not already present
        if (!updated.find(s => s.id === newStage && s.round === round)) {
          updated.push({
            id: newStage,
            label: newStage.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
            status: 'active',
            startedAt: new Date().toISOString(),
            completedAt: null,
            round,
            detail: event.detail || null,
            nodeCount: 0,
          });
        }
        return updated;
      });
      return;
    }

    // Stage result (non-streaming stages)
    if (event._patternStageResult) {
      setStageResults(prev => ({ ...prev, [event.stage]: event.data }));
      return;
    }

    // Checkpoint (branch/loop pause)
    if (event._checkpoint) {
      setCheckpoint(event);
      setExecutionId(event.executionId);
      return;
    }

    // Pattern complete
    if (event._patternComplete) {
      setExecutionId(event.executionId);
      // Mark all stages done
      setStageHistory(prev => prev.map(s =>
        s.status === 'active' ? { ...s, status: 'done', completedAt: new Date().toISOString() } : s
      ));
      // Fire onComplete callback for pipeline orchestrator
      if (onCompleteRef.current) {
        onCompleteRef.current({ status: 'complete', totalRounds: event.totalRounds, stagesExecuted: event.stagesExecuted });
        onCompleteRef.current = null;
      }
      return;
    }

    // Pattern error
    if (event._patternError) {
      if (event.fatal) {
        setError(`Stage "${event.stage}" failed: ${event.error}`);
      }
      return;
    }

    // Meta update (dynamic types from pattern generate stages)
    if (event._meta) {
      if (dynamicTypesRef) dynamicTypesRef.current = event.types || [];
      if (dynamicConfigRef && buildDynamicConfigFn && event.types) {
        dynamicConfigRef.current = buildDynamicConfigFn(event.types);
      }
      return;
    }

    // Alternative header (portfolio pattern)
    if (event._alternative) {
      // Forward as-is for PortfolioCard handling
      return;
    }

    // Regular node — add to canvas
    if (event.id && event.type) {
      const existingIds = new Set((rawNodesRef?.current || []).map(n => n.id));
      const flowNode = buildFlowNode(event, existingIds);
      if (flowNode && rawNodesRef) {
        if (dynamicConfigRef?.current) flowNode.data.dynamicConfig = dynamicConfigRef.current;
        rawNodesRef.current = [...rawNodesRef.current, flowNode];
        if (applyLayout) {
          applyLayout(rawNodesRef.current, drillStackRef?.current || []);
        }
        if (setNodeCount) setNodeCount(rawNodesRef.current.length);
      }
    }
  }, [rawNodesRef, applyLayout, drillStackRef, dynamicTypesRef, dynamicConfigRef, buildDynamicConfigFn, setNodeCount]);

  const stop = useCallback(() => {
    abortRef.current?.abort();
    setIsExecuting(false);
    setCurrentStage(null);
  }, []);

  const resume = useCallback(async (decision) => {
    if (!executionId) return;
    setCheckpoint(null);

    try {
      await authFetch(`${API_URL}/api/pattern/resume`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ executionId, decision }),
      });
    } catch (err) {
      console.error('Resume error:', err);
    }
  }, [executionId]);

  return {
    execute,
    stop,
    resume,
    isExecuting,
    currentStage,
    currentRound,
    stageResults,
    stageHistory,
    patternName,
    startedAt,
    checkpoint,
    error,
    executionId,
  };
}

export default usePatternExecutor;
